// SolyticsInputColorPicker.jsx
// @ts-nocheck
/* eslint-disable react/prop-types */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Paper,
  InputBase,
  Popover,
  IconButton,
  styled,
  Snackbar,
  Alert,
} from '@mui/material';
import { ReactComponent as ColorizeIcon } from '../../assets/Icons/lucideIcons/pipette.svg';
import SolyticsLabel from './SolyticsLabel';
import SolyticsTypography from './SolyticsTypography';
import SolyticsBox from './SolyticsBox';
import SolyticsDropdownInput from './SolyticsDropdownInput';

/**
 * SolyticsInputColorPicker - A comprehensive color picker component supporting HEX, RGB, and HSL formats
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.label="Select Color"] - Label text for the color picker
 * @param {string} [props.value] - Initial color value in HSL format (e.g., "242, 66%, 39%")
 * @param {Function} [props.onChange] - Callback function called when color changes, receives color object
 * @param {boolean} [props.disabled=false] - Whether the color picker is disabled
 * @param {string} [props.id] - HTML id attribute for the input
 * @returns {JSX.Element} Color picker component
 */

const formats = ['HEX', 'RGB', 'HSL'];
const formatOptions = formats.map((f) => ({ value: f, label: f }));

const StyledColorInput = styled(InputBase)(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 500,
  padding: '4px',
  border: `1px solid ${theme.palette.grey[60]}`,
  background: theme.palette.background.default,
  '& input': {
    textAlign: 'center',
  },
}));

const SolyticsInputColorPicker = ({
  label = 'Select Color',
  value,
  onChange,
  disabled = false,
  id = 'color-picker',
}) => {
  // Parse initial value if provided
  const parseInitialValue = (val) => {
    if (!val) return { h: 242, s: 66, l: 39, o: 100 };
    const match = val.match(/^(\d+),\s*(\d+)%,\s*(\d+)%(?:,\s*(\d+))?$/);
    if (match) {
      return {
        h: parseInt(match[1]),
        s: parseInt(match[2]),
        l: parseInt(match[3]),
        o: match[4] ? parseInt(match[4]) : 100,
      };
    }
    return { h: 242, s: 66, l: 39, o: 100 };
  };

  const initial = parseInitialValue(value);
  const [format, setFormat] = useState('HSL');
  const [hue, setHue] = useState(initial.h);
  const [saturation, setSaturation] = useState(initial.s);
  const [lightness, setLightness] = useState(initial.l);
  const [opacity, setOpacity] = useState(initial.o);

  const [anchorEl, setAnchorEl] = useState(null);
  const [pickerPosition, setPickerPosition] = useState({ x: 50, y: 50 });
  const [hexInput, setHexInput] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const gradientRef = useRef(null);

  // Convert HSL to RGB
  const hslToRgb = (h, s, l) => {
    // eslint-disable-next-line no-param-reassign
    s /= 100;
    // eslint-disable-next-line no-param-reassign
    l /= 100;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r = 0,
      g = 0,
      b = 0;

    if (h >= 0 && h < 60) {
      r = c;
      g = x;
      b = 0;
    } else if (h >= 60 && h < 120) {
      r = x;
      g = c;
      b = 0;
    } else if (h >= 120 && h < 180) {
      r = 0;
      g = c;
      b = x;
    } else if (h >= 180 && h < 240) {
      r = 0;
      g = x;
      b = c;
    } else if (h >= 240 && h < 300) {
      r = x;
      g = 0;
      b = c;
    } else if (h >= 300 && h < 360) {
      r = c;
      g = 0;
      b = x;
    }

    return [
      Math.round((r + m) * 255),
      Math.round((g + m) * 255),
      Math.round((b + m) * 255),
    ];
  };

  // Convert RGB to HEX
  const rgbToHex = (r, g, b) => {
    return (
      '#' +
      [r, g, b]
        .map((x) => {
          const hex = x.toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        })
        .join('')
    );
  };

  // Notify parent of color changes
  const notifyChange = useCallback(
    (h, s, l, o) => {
      if (onChange) {
        const [r, g, b] = hslToRgb(h, s, l);
        const hex = rgbToHex(r, g, b);
        onChange({
          hsl: `${h}, ${s}%, ${l}%`,
          rgb: `${r}, ${g}, ${b}`,
          hex: hex.replace('#', ''),
          opacity: o,
          rgba: `rgba(${r}, ${g}, ${b}, ${o / 100})`,
        });
      }
    },
    [onChange]
  );

  // Get current color in different formats
  const getCurrentColor = () => {
    const [r, g, b] = hslToRgb(hue, saturation, lightness);
    const hex = rgbToHex(r, g, b);
    return { r, g, b, hex };
  };

  const formatValue = () => {
    const { r, g, b, hex } = getCurrentColor();

    if (format === 'HEX') return hex.replace('#', '');
    if (format === 'RGB') return `${r}, ${g}, ${b}`;
    if (format === 'HSL') return `${hue}, ${saturation}%, ${lightness}%`;
  };

  const handleOpenColorPicker = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleGradientClick = (e) => {
    if (!gradientRef.current || disabled) return;

    const rect = gradientRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const newX = Math.max(0, Math.min(100, x));
    const newY = Math.max(0, Math.min(100, y));
    const newS = Math.round(newX);
    const newL = Math.round(100 - newY);

    setPickerPosition({ x: newX, y: newY });
    setSaturation(newS);
    setLightness(newL);
    notifyChange(hue, newS, newL, opacity);
  };

  const handleFormatChange = (newFormat) => {
    setFormat(newFormat);
  };

  const rgbToHsl = (r, g, b) => {
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;

    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    let h = 0,
      s = 0,
      l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case rNorm:
          h = (gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0);
          break;
        case gNorm:
          h = (bNorm - rNorm) / d + 2;
          break;
        case bNorm:
          h = (rNorm - gNorm) / d + 4;
          break;
      }
      h /= 6;
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100),
    };
  };

  const hexToHsl = (hexValue) => {
    const hex = hexValue.replace('#', '');
    if (hex.length !== 6) return null;

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    return rgbToHsl(r, g, b);
  };

  const handleEyeDropper = async () => {
    if (disabled) return;

    if (!window.EyeDropper) {
      setErrorMessage('EyeDropper API is not supported in your browser');
      return;
    }

    try {
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();

      const hsl = hexToHsl(result.sRGBHex);
      if (hsl) {
        setHue(hsl.h);
        setSaturation(hsl.s);
        setLightness(hsl.l);
        notifyChange(hsl.h, hsl.s, hsl.l, opacity);
      }
    } catch (err) {
      // User cancelled the picker - this is expected behavior
      if (err.name !== 'AbortError') {
        setErrorMessage('Failed to pick color');
      }
    }
  };

  const { r, g, b, hex } = getCurrentColor();
  const displayColor = `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;

  // Update hexInput when format changes or color changes
  useEffect(() => {
    if (format === 'HEX') {
      setHexInput(hex.replace('#', ''));
    }
  }, [format, hue, saturation, lightness, hex]);

  // Notify parent when color or opacity changes
  useEffect(() => {
    notifyChange(hue, saturation, lightness, opacity);
  }, [hue, saturation, lightness, opacity, notifyChange]);

  return (
    <SolyticsBox sx={{ width: '350px' }}>
      <SolyticsLabel label={label} id={id} />
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 48,
          borderRadius: '8px',
          border: (theme) => `1px solid ${theme.palette.grey[70]}`,
          background: (theme) =>
            disabled
              ? theme.palette.grey[40]
              : theme.palette.background.default,
          overflow: 'hidden',
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        {/* LEFT DROPDOWN */}
        <SolyticsBox
          sx={{
            width: 80,
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          <SolyticsDropdownInput
            name="format-main"
            options={formatOptions}
            value={format}
            onChange={(e) => handleFormatChange(e.target.value)}
            disabled={disabled}
            styleVariant="color-format-main"
          />
        </SolyticsBox>

        {/* MIDDLE SECTION */}
        <SolyticsBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: 1,
            pl: 1,
            pr: 1,
            borderLeft: (theme) => `1px solid ${theme.palette.grey[60]}`,
            borderRight: (theme) => `1px solid ${theme.palette.grey[60]}`,
            height: '100%',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
          onClick={disabled ? undefined : handleOpenColorPicker}
        >
          {/* COLOR SQUARE */}
          <SolyticsBox
            sx={{
              width: 22,
              height: 22,
              background: displayColor,
              borderRadius: '4px',
              border: (theme) => `1px solid ${theme.palette.grey[80]}`,
            }}
          />

          <SolyticsTypography variant="h4">{formatValue()}</SolyticsTypography>
        </SolyticsBox>

        {/* RIGHT OPACITY */}
        <SolyticsBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            pr: 1.5,
            pl: 1.5,
          }}
        >
          <InputBase
            value={opacity}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') {
                setOpacity(0);
                return;
              }
              const v = Number(val);
              if (!isNaN(v) && v >= 0 && v <= 100) {
                setOpacity(v);
              }
            }}
            disabled={disabled}
            slotProps={{
              input: {
                style: { textAlign: 'right' },
                maxLength: 3,
                'aria-label': 'Opacity percentage',
              },
            }}
            sx={{
              width: 30,
              fontSize: '14px',
              fontWeight: 500,
            }}
          />
          %
        </SolyticsBox>
      </Paper>
      {/* CUSTOM COLOR PICKER POPOVER */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              p: 2,
              width: 320,
              borderRadius: '12px',
              background: (theme) => theme.palette.background.default,
            },
          },
        }}
      >
        {/* Gradient Picker Area */}
        <SolyticsBox
          ref={gradientRef}
          onClick={handleGradientClick}
          role="slider"
          aria-label="Color saturation and lightness picker"
          aria-valuetext={`Saturation ${saturation}%, Lightness ${lightness}%`}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (disabled) return;
            const step = e.shiftKey ? 10 : 1;
            let newS = saturation;
            let newL = lightness;

            if (e.key === 'ArrowRight') {
              newS = Math.min(100, saturation + step);
              e.preventDefault();
            } else if (e.key === 'ArrowLeft') {
              newS = Math.max(0, saturation - step);
              e.preventDefault();
            } else if (e.key === 'ArrowUp') {
              newL = Math.min(100, lightness + step);
              e.preventDefault();
            } else if (e.key === 'ArrowDown') {
              newL = Math.max(0, lightness - step);
              e.preventDefault();
            }

            if (newS !== saturation || newL !== lightness) {
              setSaturation(newS);
              setLightness(newL);
              setPickerPosition({ x: newS, y: 100 - newL });
              notifyChange(hue, newS, newL, opacity);
            }
          }}
          sx={{
            width: '100%',
            height: 130,
            borderRadius: '8px',
            background: `linear-gradient(to bottom, transparent, black),
                        linear-gradient(to right, white, hsl(${hue}, 100%, 50%))`,
            position: 'relative',
            cursor: disabled ? 'not-allowed' : 'crosshair',
            mb: 2,
            '&:focus-visible': {
              outline: (theme) => `2px solid ${theme.palette.lavenderBlue[80]}`,
              outlineOffset: '2px',
            },
          }}
        >
          {/* Picker Circle */}
          <SolyticsBox
            sx={{
              position: 'absolute',
              left: `${pickerPosition.x}%`,
              top: `${pickerPosition.y}%`,
              width: 16,
              height: 16,
              borderRadius: '50%',
              border: '2px solid white',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
            }}
          />
        </SolyticsBox>

        {/* Eye Dropper and Sliders */}
        <SolyticsBox
          sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}
        >
          <IconButton
            onClick={handleEyeDropper}
            disabled={disabled}
            aria-label="Pick color from screen"
            sx={{
              width: 32,
              height: 32,
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ColorizeIcon
              sx={{ fontSize: 18, color: (theme) => theme.palette.grey.T30 }}
            />
          </IconButton>

          <SolyticsBox sx={{ flex: 1 }}>
            {/* Hue Slider */}
            <SolyticsBox sx={{ mb: 1.5 }}>
              <SolyticsBox sx={{ position: 'relative', height: 12 }}>
                <SolyticsBox
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '6px',
                    background:
                      'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
                  }}
                />
                <input
                  type="range"
                  min="0"
                  max="360"
                  value={hue}
                  onChange={(e) => {
                    const newHue = Number(e.target.value);
                    setHue(newHue);
                    notifyChange(newHue, saturation, lightness, opacity);
                  }}
                  disabled={disabled}
                  aria-label="Hue"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                  }}
                />
                <SolyticsBox
                  sx={{
                    position: 'absolute',
                    left: `${(hue / 360) * 100}%`,
                    top: '50%',
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: '2px solid white',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    background: `hsl(${hue}, 100%, 50%)`,
                  }}
                />
              </SolyticsBox>
            </SolyticsBox>

            {/* Opacity Slider */}
            <SolyticsBox>
              <SolyticsBox sx={{ position: 'relative', height: 12 }}>
                <SolyticsBox
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '6px',
                    background: `linear-gradient(to right, transparent, ${hex})`,
                    backgroundImage: `
                  linear-gradient(to right, transparent, ${hex}),
                  repeating-conic-gradient(#cccccc 0% 25%, white 0% 50%) 50% / 8px 8px
                `,
                  }}
                />
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={opacity}
                  onChange={(e) => {
                    const newOpacity = Number(e.target.value);
                    setOpacity(newOpacity);
                    notifyChange(hue, saturation, lightness, newOpacity);
                  }}
                  disabled={disabled}
                  aria-label="Opacity"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                  }}
                />
                <SolyticsBox
                  sx={{
                    position: 'absolute',
                    left: `${opacity}%`,
                    top: '50%',
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: '2px solid white',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    background: displayColor,
                  }}
                />
              </SolyticsBox>
            </SolyticsBox>
          </SolyticsBox>
        </SolyticsBox>

        {/* Color Values Inputs */}
        <SolyticsBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '1px',
            alignSelf: 'stretch',
          }}
        >
          <SolyticsDropdownInput
            name="format-popover"
            size="small"
            options={formatOptions}
            value={format}
            onChange={(e) => handleFormatChange(e.target.value)}
            disabled={disabled}
            styleVariant="color-format-popover"
          />

          {format === 'HSL' && (
            <>
              <StyledColorInput
                value={hue}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') return;
                  const v = Number(val);
                  if (!isNaN(v) && v >= 0 && v <= 360) {
                    setHue(v);
                    notifyChange(v, saturation, lightness, opacity);
                  }
                }}
                disabled={disabled}
                slotProps={{ input: { maxLength: 3, 'aria-label': 'Hue' } }}
                sx={{ flex: 0.7, borderRadius: '4px 0 0 4px' }}
              />
              <StyledColorInput
                value={saturation}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') return;
                  const v = Number(val);
                  if (!isNaN(v) && v >= 0 && v <= 100) {
                    setSaturation(v);
                    notifyChange(hue, v, lightness, opacity);
                  }
                }}
                disabled={disabled}
                slotProps={{
                  input: { maxLength: 3, 'aria-label': 'Saturation' },
                }}
                sx={{ flex: 0.7, borderRadius: '0px' }}
              />
              <StyledColorInput
                value={lightness}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') return;
                  const v = Number(val);
                  if (!isNaN(v) && v >= 0 && v <= 100) {
                    setLightness(v);
                    notifyChange(hue, saturation, v, opacity);
                  }
                }}
                disabled={disabled}
                slotProps={{
                  input: { maxLength: 3, 'aria-label': 'Lightness' },
                }}
                sx={{ flex: 0.7, borderRadius: '0px' }}
              />
            </>
          )}

          {format === 'RGB' && (
            <>
              <StyledColorInput
                value={r}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') return;
                  const v = Number(val);
                  if (!isNaN(v) && v >= 0 && v <= 255) {
                    const hsl = rgbToHsl(v, g, b);
                    setHue(hsl.h);
                    setSaturation(hsl.s);
                    setLightness(hsl.l);
                    notifyChange(hsl.h, hsl.s, hsl.l, opacity);
                  }
                }}
                disabled={disabled}
                slotProps={{ input: { maxLength: 3, 'aria-label': 'Red' } }}
                sx={{ flex: 0.7, borderRadius: '4px 0 0 4px' }}
              />
              <StyledColorInput
                value={g}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') return;
                  const v = Number(val);
                  if (!isNaN(v) && v >= 0 && v <= 255) {
                    const hsl = rgbToHsl(r, v, b);
                    setHue(hsl.h);
                    setSaturation(hsl.s);
                    setLightness(hsl.l);
                    notifyChange(hsl.h, hsl.s, hsl.l, opacity);
                  }
                }}
                disabled={disabled}
                slotProps={{ input: { maxLength: 3, 'aria-label': 'Green' } }}
                sx={{ flex: 0.7, borderRadius: '0px' }}
              />
              <StyledColorInput
                value={b}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') return;
                  const v = Number(val);
                  if (!isNaN(v) && v >= 0 && v <= 255) {
                    const hsl = rgbToHsl(r, g, v);
                    setHue(hsl.h);
                    setSaturation(hsl.s);
                    setLightness(hsl.l);
                    notifyChange(hsl.h, hsl.s, hsl.l, opacity);
                  }
                }}
                disabled={disabled}
                slotProps={{ input: { maxLength: 3, 'aria-label': 'Blue' } }}
                sx={{ flex: 0.7, borderRadius: '0px 4px 4px 0' }}
              />
            </>
          )}

          {format === 'HEX' && (
            <StyledColorInput
              value={`#${hexInput}`}
              onChange={(e) => {
                let hexValue = e.target.value.replace('#', '').toUpperCase();
                if (/^[0-9A-Fa-f]{0,6}$/.test(hexValue)) {
                  setHexInput(hexValue);
                  if (hexValue.length === 6) {
                    const hsl = hexToHsl(hexValue);
                    if (hsl) {
                      setHue(hsl.h);
                      setSaturation(hsl.s);
                      setLightness(hsl.l);
                      notifyChange(hsl.h, hsl.s, hsl.l, opacity);
                    }
                  }
                }
              }}
              disabled={disabled}
              slotProps={{ input: { maxLength: 7, 'aria-label': 'Hex color' } }}
              sx={{ flex: 1, borderRadius: '4px' }}
            />
          )}

          <StyledColorInput
            value={opacity}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') {
                setOpacity(0);
                return;
              }
              const v = Number(val);
              if (!isNaN(v) && v >= 0 && v <= 100) {
                setOpacity(v);
                notifyChange(hue, saturation, lightness, v);
              }
            }}
            disabled={disabled}
            endAdornment={
              <SolyticsTypography variant="h4">%</SolyticsTypography>
            }
            slotProps={{ input: { maxLength: 3, 'aria-label': 'Opacity' } }}
            sx={{ flex: 0.7, borderRadius: '0 4px 4px 0' }}
          />
        </SolyticsBox>
      </Popover>
      {/* Error notification */}
      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={4000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setErrorMessage('')}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </SolyticsBox>
  );
};

export default SolyticsInputColorPicker;
